/**
 * useNotifications.ts
 *
 * React hook that owns polling + loading/error state for a header notifications
 * bell, composed on top of NotificationApiClient. This file makes no `fetch()`
 * calls of its own — all transport is delegated to the client (SRP: this hook's
 * only job is "when to fetch and what to do with the result", not "how to fetch").
 *
 * Delivery mechanism is polling, not push (confirmed design decision — see
 * Documentation/Projects/ui-app-templates/auth-header-controls-design.md §2.8: "The
 * consuming app polls GetUnreadAsync on an interval... no SignalR/EdgeStream wiring
 * is needed for this feature").
 *
 * IMPORTANT — no markAsRead here, intentionally: BaseNotificationController.cs (the
 * real backend controller, BizFirstPayrollV3 repo) has no [Http...] route for
 * MarkReadAsync even though INotificationService declares it. A `markAsRead`/
 * `markAllAsRead` method on this hook's returned object would have nothing to call
 * and would either silently no-op (a fake success) or throw on every use. Once a
 * real route exists on the backend, add it to NotificationApiClient first, then
 * surface it here.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NotificationApiClient, NotificationApiError } from '../api/notificationApiClient'
import type { IDInfo, NotificationItem, NotificationPageInfoRequest } from '../types'

/** 30s balances "badge feels reasonably live" against "don't hammer the backend
 *  from every open tab of every logged-in user." Chosen as a sensible default for
 *  a header bell, not derived from any backend rate-limit figure — callers with
 *  different needs should override via `pollIntervalMs`. */
const DEFAULT_POLL_INTERVAL_MS = 30_000

/** Recent-items page size shown in a bell dropdown. Deliberately small — this is a
 *  preview list, not a full notifications page (the design's `viewAllUrl` link is
 *  what a full page is for). */
const DEFAULT_PAGE_SIZE = 10

export interface UseNotificationsOptions {
  /** Base URL of the Communication API. Passed straight through to
   *  NotificationApiClient — this hook stays configuration-agnostic itself. */
  apiBaseUrl: string
  /** Returns the current bearer token, or null/undefined if unavailable. Invoked
   *  fresh on every poll tick, same contract as NotificationApiClientOptions. */
  getAuthToken: () => string | null | undefined
  /**
   * When supplied, the recent-items list is fetched via `search/by-user` (which
   * includes both read and unread notifications, giving a fuller "recent activity"
   * preview) and `unreadCount` is fetched separately via `search/unread`'s
   * `pageInfo.totalRecords` (requested with a minimal page size, since only the
   * count is needed for that call).
   *
   * When omitted, the hook falls back to a single `search/unread` call: `items`
   * becomes that call's unread notifications and `unreadCount` is its
   * `pageInfo.totalRecords` — fewer requests, at the cost of `items` only ever
   * showing unread notifications (no read history). This is a reasonable default
   * for a bell widget and matches the design doc's own description of the polling
   * contract (poll GetUnreadAsync, feed items+unreadCount from it).
   */
  userID?: IDInfo
  /** Recent-items page size. Defaults to 10. */
  pageSize?: number
  /** Poll interval in milliseconds. Defaults to 30000 (see DEFAULT_POLL_INTERVAL_MS
   *  above for the reasoning). */
  pollIntervalMs?: number
  /** Set to false to pause fetching/polling entirely (e.g. while the consuming
   *  app's own auth state is still hydrating, or the user is logged out). Defaults
   *  to true. */
  enabled?: boolean
}

export interface UseNotificationsResult {
  /** Recent notifications — see `userID` option above for what determines this
   *  list's contents (recent activity vs. unread-only). */
  items: NotificationItem[]
  /** Total unread count, from `search/unread`'s `pageInfo.totalRecords` — always
   *  the true unread count regardless of how many `items` were fetched. */
  unreadCount: number
  /** True only for the initial fetch (or the first fetch after `enabled` flips to
   *  true); background poll ticks update `items`/`unreadCount` without flipping
   *  this back to true, so the UI doesn't flash a loading state every 30s. */
  isLoading: boolean
  error: NotificationApiError | Error | null
  /** Triggers an immediate fetch outside the poll interval (e.g. after the user
   *  performs an action expected to change their notifications). Does not reset or
   *  restart the interval timer. */
  refetch: () => Promise<void>
}

/**
 * Polls the notifications backend and exposes the current unread state for a
 * header bell widget. Stops polling on unmount; skips a poll tick (but keeps the
 * timer running) while the tab is hidden, per the Page Visibility API, so
 * background tabs don't spend network/backend resources on a badge nobody can see.
 */
export function useNotifications(options: UseNotificationsOptions): UseNotificationsResult {
  const { apiBaseUrl, getAuthToken, userID, pageSize = DEFAULT_PAGE_SIZE, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS, enabled = true } = options

  // getAuthToken is invoked through a ref indirection so that the memoized client
  // below (recreated only when apiBaseUrl changes) always calls whatever the
  // latest render passed in, even if the caller supplies a new function reference
  // on every render (a common pattern for inline `() => store.getState().token`
  // callbacks) rather than a stable one.
  const getAuthTokenRef = useRef(getAuthToken)
  getAuthTokenRef.current = getAuthToken

  const client = useMemo(
    () => new NotificationApiClient({ apiBaseUrl, getAuthToken: () => getAuthTokenRef.current() }),
    [apiBaseUrl]
  )

  // `userID` is an IDInfo object, which callers will very commonly pass as a fresh
  // inline literal (`userID={{ id: user.id, resID: '' }}`) on every render. Using
  // it directly in a dependency array would recreate `fetchNow` — and therefore
  // re-run the polling effect below — on every single render, not just every
  // `pollIntervalMs`. Depending on a derived primitive key instead avoids that,
  // while a ref keeps `fetchNow` reading the latest object value at call time.
  const userIDKey = userID ? `${userID.id}:${userID.resID}` : null
  const userIDRef = useRef(userID)
  userIDRef.current = userID

  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<NotificationApiError | Error | null>(null)

  const isMountedRef = useRef(true)
  const isFetchingRef = useRef(false)
  const hasFetchedOnceRef = useRef(false)

  const fetchNow = useCallback(async () => {
    if (isFetchingRef.current) return

    // A token isn't available yet (e.g. auth still hydrating, or the user is
    // logged out) — skip silently rather than firing a request guaranteed to
    // 401. Not treated as an error state; the next tick tries again.
    if (!getAuthTokenRef.current()) return

    isFetchingRef.current = true
    if (!hasFetchedOnceRef.current) {
      setIsLoading(true)
    }

    try {
      const recentPageInfo: NotificationPageInfoRequest = { pageNumber: 1, pageSize }
      const currentUserID = userIDRef.current

      if (currentUserID) {
        const [byUserResult, unreadResult] = await Promise.all([
          client.searchByUser(currentUserID, { pageInfo: recentPageInfo }),
          // Only the count is needed here; keep the payload minimal.
          client.searchUnread({ pageInfo: { pageNumber: 1, pageSize: 1 } }),
        ])
        if (!isMountedRef.current) return
        setItems(byUserResult.items)
        setUnreadCount(unreadResult.pageInfo.totalRecords)
      } else {
        const unreadResult = await client.searchUnread({ pageInfo: recentPageInfo })
        if (!isMountedRef.current) return
        setItems(unreadResult.items)
        setUnreadCount(unreadResult.pageInfo.totalRecords)
      }

      if (isMountedRef.current) {
        setError(null)
      }
    } catch (fetchError) {
      if (isMountedRef.current) {
        setError(fetchError instanceof Error ? fetchError : new Error('Failed to fetch notifications'))
      }
    } finally {
      hasFetchedOnceRef.current = true
      isFetchingRef.current = false
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [client, userIDKey, pageSize])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    // fetchNow() itself no-ops (see above) while getAuthTokenRef.current() is
    // falsy, so it's safe to unconditionally kick off the interval here even
    // before a token is available — later ticks pick it up automatically once
    // the caller's getAuthToken starts returning one.
    void fetchNow()

    const intervalId = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return
      void fetchNow()
    }, pollIntervalMs)

    return () => clearInterval(intervalId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollIntervalMs, fetchNow])

  return { items, unreadCount, isLoading, error, refetch: fetchNow }
}
